'use client';

import { useEffect, useRef } from 'react';

export default function ParticleEffect() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isSmallScreen = window.innerWidth < 768;
    if (prefersReducedMotion || isSmallScreen) return;

    // 创建两个canvas元素，一个用于背景粒子，一个用于流体粒子
    // 1. 背景粒子canvas
    const bgCanvas = document.createElement('canvas');
    bgCanvas.id = 'background-particles';
    bgCanvas.style.position = 'fixed';
    bgCanvas.style.top = '0';
    bgCanvas.style.left = '0';
    bgCanvas.style.width = '100%';
    bgCanvas.style.height = '100%';
    bgCanvas.style.zIndex = '1';
    bgCanvas.style.opacity = '0.5'; // 降低整体不透明度
    bgCanvas.style.pointerEvents = 'none';
    
    // 2. 流体粒子canvas (鼠标交互)
    const fluidCanvas = document.createElement('canvas');
    fluidCanvas.id = 'fluid-particles';
    fluidCanvas.style.position = 'fixed';
    fluidCanvas.style.top = '0';
    fluidCanvas.style.left = '0';
    fluidCanvas.style.width = '100%';
    fluidCanvas.style.height = '100%';
    fluidCanvas.style.zIndex = '2';
    fluidCanvas.style.opacity = '0.9';
    fluidCanvas.style.pointerEvents = 'none';
    
    // 添加两个canvas到容器
    container.appendChild(bgCanvas);
    container.appendChild(fluidCanvas);

    // 设置canvas大小
    const setCanvasSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      bgCanvas.width = width;
      bgCanvas.height = height;
      
      fluidCanvas.width = width;
      fluidCanvas.height = height;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // 初始化上下文
    const bgCtx = bgCanvas.getContext('2d');
    const fluidCtx = fluidCanvas.getContext('2d');

    // 背景粒子类
    class BackgroundParticle {
      constructor() {
        this.x = Math.random() * bgCanvas.width;
        this.y = Math.random() * bgCanvas.height;
        // 减小背景粒子尺寸
        this.size = Math.random() * 1.0 + 0.3; // 0.3-1.3像素
        this.baseSize = this.size;
        this.speedX = (Math.random() - 0.5) * 0.3; // 缓慢移动
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        this.color = '#CCAA44'; // 降低颜色亮度
        this.opacity = Math.random() * 0.4 + 0.2; // 降低不透明度
      }

      update() {
        // 检查是否在鼠标附近
        if (mouse.x && mouse.y) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // 如果在鼠标影响范围内
          if (distance < mouse.radius) {
            // 计算鼠标对粒子的轻微排斥力
            const force = (mouse.radius - distance) / mouse.radius;
            const directionX = dx / distance || 0;
            const directionY = dy / distance || 0;
            
            // 应用排斥力，粒子沿相反方向移动
            this.speedX -= directionX * force * 0.1;
            this.speedY -= directionY * force * 0.1;
            
            // 轻微增加粒子大小
            this.size = this.baseSize + (this.baseSize * force * 0.5);
          } else {
            // 重置为原始速度
            this.speedX = (this.speedX + this.baseSpeedX) * 0.5;
            this.speedY = (this.speedY + this.baseSpeedY) * 0.5;
            this.size = Math.max(this.baseSize, this.size * 0.95);
          }
        }

        this.x += this.speedX;
        this.y += this.speedY;

        // 限制粒子速度
        this.speedX = Math.max(-1.5, Math.min(1.5, this.speedX));
        this.speedY = Math.max(-1.5, Math.min(1.5, this.speedY));

        // 边界检查，如果粒子超出边界，从另一侧重新进入
        if (this.x > bgCanvas.width) this.x = 0;
        else if (this.x < 0) this.x = bgCanvas.width;
        
        if (this.y > bgCanvas.height) this.y = 0;
        else if (this.y < 0) this.y = bgCanvas.height;
      }

      draw() {
        // 对于较小的粒子，使用1像素的点绘制而不是圆形
        if (this.size < 0.8) {
          bgCtx.fillStyle = this.color;
          bgCtx.globalAlpha = this.opacity;
          bgCtx.fillRect(Math.floor(this.x), Math.floor(this.y), 1, 1);
        } else {
          bgCtx.beginPath();
          bgCtx.fillStyle = this.color;
          bgCtx.globalAlpha = this.opacity;
          bgCtx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          bgCtx.fill();
        }
      }
    }

    // 流体粒子类
    class FluidParticle {
      constructor() {
        this.velocity = { x: 0, y: 0 }; // 初始化速度向量属性
        this.reset();
      }

      reset() {
        // 确保velocity对象已初始化
        if (!this.velocity) {
          this.velocity = { x: 0, y: 0 };
        }
        
        // 微小粒子，适合水蒸汽效果
        if (Math.random() > 0.2) {
          this.size = Math.random() * 0.12 + 0.02; // 0.02-0.14像素
        } else {
          this.size = Math.random() * 0.15 + 0.05; // 0.05-0.2像素
        }
        
        // 少量更大的粒子，增加层次感
        if (Math.random() > 0.95) {
          this.size = Math.random() * 0.3 + 0.1; // 0.1-0.4像素
        }
        
        this.baseSize = this.size;
        this.x = Math.random() * fluidCanvas.width;
        this.y = Math.random() * fluidCanvas.height;
        
        // 初始速度更随机，方向性更强
        const speed = Math.random() * 2 + 1;
        const angle = Math.random() * Math.PI * 2;
        this.velocity.x = Math.cos(angle) * speed;
        this.velocity.y = Math.sin(angle) * speed;
        
        this.color = '#CCAA44';
        this.alpha = Math.random() * 0.7 + 0.2;
        this.life = Math.random() * 15 + 5;
        this.maxLife = this.life;
        this.decay = Math.random() * 0.15 + 0.1;
        this.waveAngle = Math.random() * Math.PI * 2;
        this.waveFrequency = Math.random() * 0.15 + 0.1;
        this.waveAmplitude = Math.random() * 5 + 2;
        
        // 随机初始分布属性
        this.spread = Math.random() * 10 + 5; // 粒子在生成时的分布范围
        this.inertia = 0.92 + Math.random() * 0.03; // 保持惯性的程度
      }

      update() {
        // 对流体粒子保持惯性运动
        this.waveAngle += this.waveFrequency;
        
        // 添加微小的随机性和波动，但保持主要方向
        this.velocity.x += Math.sin(this.waveAngle) * this.waveAmplitude * 0.01;
        this.velocity.y += Math.cos(this.waveAngle) * this.waveAmplitude * 0.01;
        
        // 保持现有惯性
        this.velocity.x *= this.inertia;
        this.velocity.y *= this.inertia;
        
        this.x += this.velocity.x;
        this.y += this.velocity.y;
        
        // 鼠标交互 - 轻微的吸引/排斥力
        if (mouse.x && mouse.y) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < mouse.radius) {
            // 计算力量和方向
            const force = (mouse.radius - distance) / mouse.radius * 0.2;
            const directionX = dx / distance || 0;
            const directionY = dy / distance || 0;
            
            // 根据鼠标是否移动决定吸引还是排斥
            if (mouse.isMoving) {
              // 轻微的吸引力 - 跟随鼠标移动方向
              const moveAngle = Math.atan2(mouse.speedY, mouse.speedX);
              this.velocity.x += Math.cos(moveAngle) * force;
              this.velocity.y += Math.sin(moveAngle) * force;
            } else {
              // 轻微的扩散力 - 从中心向外扩散
              this.velocity.x -= directionX * force * 0.5;
              this.velocity.y -= directionY * force * 0.5;
            }
            
            // 轻微增加粒子生命值
            this.life = Math.min(this.maxLife, this.life + force * 0.5);
            this.alpha = Math.min(0.8, this.alpha + force * 0.1);
          }
        }
        
        // 生命周期自然递减
        this.life -= this.decay;
        this.alpha = (this.life / this.maxLife) * 0.6;
        this.size *= 0.97; // 粒子大小缓慢减小
        
        // 如果生命耗尽，立即移除
        if (this.life <= 0 || this.size < 0.01) {
          return false;
        }
        
        // 边界检查
        if (this.x > fluidCanvas.width || this.x < 0 || this.y > fluidCanvas.height || this.y < 0) {
          return false;
        }
        
        return true;
      }

      draw() {
        // 对于极小的流体粒子，使用点绘制而非圆形
        if (this.size < 0.2) {
          fluidCtx.globalAlpha = this.alpha;
          fluidCtx.fillStyle = `rgba(255, 220, 0, ${this.alpha})`;
          fluidCtx.fillRect(Math.floor(this.x), Math.floor(this.y), 1, 1);
        } else {
          fluidCtx.beginPath();
          fluidCtx.globalAlpha = this.alpha;
          
          const gradient = fluidCtx.createRadialGradient(
            this.x, this.y, 0,
            this.x, this.y, this.size
          );
          
          gradient.addColorStop(0, `rgba(255, 220, 0, ${this.alpha * 1.2})`);
          gradient.addColorStop(0.5, `rgba(255, 180, 0, ${this.alpha * 0.8})`);
          gradient.addColorStop(1, `rgba(182, 141, 64, 0)`);
          
          fluidCtx.fillStyle = gradient;
          fluidCtx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          fluidCtx.fill();
        }
      }
    }

    // 创建粒子数组
    const bgParticles = [];
    const fluidParticles = [];
    
    // 减少背景粒子数量
    const bgParticleCount = 120; // 从180减少到120
    
    // 初始流体粒子数量
    const initialFluidParticleCount = 50;

    // 添加背景粒子
    for (let i = 0; i < bgParticleCount; i++) {
      bgParticles.push(new BackgroundParticle());
    }
    
    // 添加初始流体粒子
    for (let i = 0; i < initialFluidParticleCount; i++) {
      fluidParticles.push(new FluidParticle());
    }

    // 处理鼠标交互
    const mouse = {
      x: null,
      y: null,
      radius: 120,
      lastX: null,
      lastY: null,
      lastMoveTime: 0,
      isMoving: false,
      speedX: 0,
      speedY: 0,
      movementHistory: []
    };

    // 鼠标移动事件
    function handleMouseMove(event) {
      const currentTime = Date.now();
      mouse.lastX = mouse.x;
      mouse.lastY = mouse.y;
      
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      
      // 计算鼠标速度
      if (mouse.lastX !== null && mouse.lastY !== null) {
        mouse.speedX = mouse.x - mouse.lastX;
        mouse.speedY = mouse.y - mouse.lastY;
      } else {
        mouse.speedX = 0;
        mouse.speedY = 0;
      }
      
      // 追踪鼠标移动历史
      if (mouse.lastX !== null && mouse.lastY !== null) {
        mouse.movementHistory.push({
          x: mouse.x,
          y: mouse.y,
          time: currentTime,
          speedX: mouse.speedX,
          speedY: mouse.speedY
        });
        
        // 只保留最近的10个历史点
        if (mouse.movementHistory.length > 10) {
          mouse.movementHistory.shift();
        }
      }
      
      // 检测鼠标是否正在移动
      if (mouse.lastX !== null) {
        const distanceMoved = Math.sqrt(
          Math.pow(mouse.x - mouse.lastX, 2) + 
          Math.pow(mouse.y - mouse.lastY, 2)
        );
        
        // 如果距离足够大，表示鼠标正在移动
        if (distanceMoved > 1) {
          mouse.isMoving = true;
          mouse.lastMoveTime = currentTime;
          
          // 鼠标移动时，创建新粒子
          const moveSpeed = Math.min(25, distanceMoved * 0.5);
          
          // 根据移动速度调整粒子数量
          let particleBurst = Math.min(2000, Math.floor(Math.pow(moveSpeed, 1.5) * 3) + 40);
          
          // 如果移动非常快，大幅增加粒子数量
          if (moveSpeed > 10) {
            particleBurst = Math.min(3000, particleBurst * 1.8);
          }
          
          const directionX = mouse.speedX;
          const directionY = mouse.speedY;
          const moveAngle = Math.atan2(directionY, directionX);
          
          for (let i = 0; i < particleBurst; i++) {
            const p = new FluidParticle();
            
            // 在鼠标周围区域内随机生成粒子，但保持均匀分布
            // 使用高斯分布而不是简单的圆形分布
            let radius, angle;
            
            // 70%的粒子在移动方向前方生成
            if (Math.random() > 0.3) {
              // 在前进方向的半圆内生成，半径在5-120像素之间
              angle = moveAngle + (Math.random() - 0.5) * Math.PI; 
              // 使用二次分布使粒子更多地分布在外围
              radius = 5 + Math.pow(Math.random(), 0.5) * 115;
            } else {
              // 30%的粒子完全随机分布
              angle = Math.random() * Math.PI * 2;
              radius = Math.random() * 120 + 5;
            }
            
            // 计算位置
            p.x = mouse.x + Math.cos(angle) * radius;
            p.y = mouse.y + Math.sin(angle) * radius;
            
            // 设置初始速度 - 保持一定的方向性和惯性
            const velocityFactor = 0.1 + Math.random() * 0.2;
            p.velocity.x = mouse.speedX * velocityFactor + (Math.random() - 0.5) * 2;
            p.velocity.y = mouse.speedY * velocityFactor + (Math.random() - 0.5) * 2;
            
            // 根据移动速度调整粒子生命周期
            const speedFactor = Math.min(1.5, moveSpeed / 10);
            p.life = (Math.random() * 12 + 8) * speedFactor;
            p.maxLife = p.life;
            p.decay = 0.1 + Math.random() * 0.15; // 自然衰减速度
            p.inertia = 0.91 + Math.random() * 0.04; // 更高的惯性保持
            
            fluidParticles.push(p);
          }
          
          // 在鼠标轨迹上也生成一些粒子
          if (mouse.movementHistory.length >= 2) {
            const trailGap = Math.max(1, Math.floor(particleBurst / 100));
            
            for (let i = 0; i < mouse.movementHistory.length - 1; i++) {
              const current = mouse.movementHistory[i];
              const next = mouse.movementHistory[i + 1];
              
              // 计算两点之间的距离
              const segmentDist = Math.sqrt(
                Math.pow(next.x - current.x, 2) + 
                Math.pow(next.y - current.y, 2)
              );
              
              // 根据距离在线段上生成粒子
              const segmentParticles = Math.ceil(segmentDist / trailGap);
              
              for (let j = 0; j < segmentParticles; j++) {
                // 在线段上插值位置
                const t = j / segmentParticles;
                const trailX = current.x + (next.x - current.x) * t;
                const trailY = current.y + (next.y - current.y) * t;
                
                // 在轨迹点周围随机偏移
                const offsetRadius = Math.random() * 20;
                const offsetAngle = Math.random() * Math.PI * 2;
                
                const p = new FluidParticle();
                p.x = trailX + Math.cos(offsetAngle) * offsetRadius;
                p.y = trailY + Math.sin(offsetAngle) * offsetRadius;
                
                // 设置速度 - 从历史速度中获取方向
                const histSpeed = Math.sqrt(current.speedX * current.speedX + current.speedY * current.speedY);
                const histAngle = Math.atan2(current.speedY, current.speedX);
                const speedVariation = 0.3 + Math.random() * 0.7; // 30%-100%的历史速度
                
                p.velocity.x = Math.cos(histAngle) * histSpeed * speedVariation;
                p.velocity.y = Math.sin(histAngle) * histSpeed * speedVariation;
                
                // 轨迹粒子寿命较短
                p.life = Math.random() * 8 + 4;
                p.maxLife = p.life;
                p.decay = 0.1 + Math.random() * 0.2;
                p.inertia = 0.9 + Math.random() * 0.05;
                
                fluidParticles.push(p);
              }
            }
          }
        } else {
          // 如果鼠标移动很小但尚未被标记为停止
          if (currentTime - mouse.lastMoveTime > 50) {
            mouse.isMoving = false;
            // 鼠标停止时速度逐渐减小
            mouse.speedX *= 0.95;
            mouse.speedY *= 0.95;
          }
        }
      }
      
      // 限制最大流体粒子数量
      if (fluidParticles.length > 5000) {
        fluidParticles.splice(0, fluidParticles.length - 5000);
      }
    }

    // 鼠标离开事件
    function handleMouseLeave() {
      mouse.lastX = mouse.x;
      mouse.lastY = mouse.y;
      mouse.x = null;
      mouse.y = null;
      mouse.isMoving = false;
      mouse.movementHistory = [];
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseLeave);
    window.addEventListener('touchmove', (e) => {
      handleMouseMove({
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY
      });
    }, { passive: true });
    window.addEventListener('touchend', handleMouseLeave);

    // 连接背景粒子
    function connectBackgroundParticles() {
      const maxDistance = 60; // 减少连接距离
      for (let a = 0; a < bgParticles.length; a++) {
        for (let b = a; b < bgParticles.length; b++) {
          const dx = bgParticles[a].x - bgParticles[b].x;
          const dy = bgParticles[a].y - bgParticles[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < maxDistance) {
            const opacity = 1 - (distance / maxDistance);
            bgCtx.strokeStyle = "rgba(182, 141, 64, " + opacity * 0.08 + ")"; // 减少连接线不透明度
            bgCtx.lineWidth = 0.2; // 减少线宽
            bgCtx.beginPath();
            bgCtx.moveTo(bgParticles[a].x, bgParticles[a].y);
            bgCtx.lineTo(bgParticles[b].x, bgParticles[b].y);
            bgCtx.stroke();
          }
        }
      }
    }

    // 背景粒子动画循环
    function animateBackground() {
      requestAnimationFrame(animateBackground);
      bgCtx.clearRect(0, 0, bgCanvas.width, bgCanvas.height);
      
      // 更新和绘制所有背景粒子
      for (let i = 0; i < bgParticles.length; i++) {
        bgParticles[i].update();
        bgParticles[i].draw();
      }
      
      // 连接背景粒子
      connectBackgroundParticles();
    }

    // 流体粒子动画循环
    function animateFluid() {
      requestAnimationFrame(animateFluid);
      fluidCtx.clearRect(0, 0, fluidCanvas.width, fluidCanvas.height);
      
      // 使用混合模式创造更明亮、更烟雾状的效果
      fluidCtx.globalCompositeOperation = 'lighter';
      
      // 更新和绘制所有流体粒子
      const remainingParticles = [];
      
      for (let i = 0; i < fluidParticles.length; i++) {
        const isAlive = fluidParticles[i].update();
        
        if (isAlive) {
          fluidParticles[i].draw();
          remainingParticles.push(fluidParticles[i]);
        }
      }
      
      // 替换原数组为剩余存活的粒子
      if (remainingParticles.length < fluidParticles.length) {
        fluidParticles.length = 0;
        fluidParticles.push(...remainingParticles);
      }
      
      // 恢复正常混合模式
      fluidCtx.globalCompositeOperation = 'source-over';
      
      // 如果鼠标已经停止移动超过200毫秒且粒子聚集在一起，让它们更快消失
      if (!mouse.isMoving && Date.now() - mouse.lastMoveTime > 200) {
        // 统计粒子的平均位置
        let avgX = 0, avgY = 0, count = 0;
        const avgRadius = 30; // 检测聚集的半径
        
        // 只计算流体粒子的平均位置
        for (let i = 0; i < fluidParticles.length; i++) {
          avgX += fluidParticles[i].x;
          avgY += fluidParticles[i].y;
          count++;
        }
        
        if (count > 0) {
          avgX /= count;
          avgY /= count;
          
          // 计算粒子到平均位置的距离
          for (let i = 0; i < fluidParticles.length; i++) {
            const p = fluidParticles[i];
            const dx = p.x - avgX;
            const dy = p.y - avgY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // 如果粒子聚集在一起，给它们一个向外扩散的力
            if (distance < avgRadius) {
              // 向外的方向
              const angle = Math.atan2(dy, dx);
              // 添加一个向外扩散的力
              p.velocity.x += Math.cos(angle) * 0.2;
              p.velocity.y += Math.sin(angle) * 0.2;
              // 增加衰减速度
              p.decay *= 1.05;
            }
          }
        }
        
        // 粒子自然消失的速度略微增加
        for (let i = 0; i < fluidParticles.length; i++) {
          fluidParticles[i].life -= 0.2;
          fluidParticles[i].size *= 0.97;
        }
      }
    }

    // 启动两个动画循环
    animateBackground();
    animateFluid();

    // 清理函数
    return () => {
      window.removeEventListener('resize', setCanvasSize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseLeave);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseLeave);
      if (bgCanvas && bgCanvas.parentNode) {
        bgCanvas.parentNode.removeChild(bgCanvas);
      }
      if (fluidCanvas && fluidCanvas.parentNode) {
        fluidCanvas.parentNode.removeChild(fluidCanvas);
      }
    };
  }, []);

  return <div ref={containerRef} className="fixed inset-0 z-0 pointer-events-none" />;
} 
